import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';
import appApi from '../../../../api/app-api';
import { displayErrorText } from '../../../../api/errors/api-error-codes';
import { JournalEntryDatRecordInput } from '../../../../api/types/accounting/journal-entries/journal-entry-dat-record-input';
import harediLogo from '../../../../assets/logos/haredi-logo.png';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Format `YYYY-MM-DD` / Date without timezone shifts for display. */
const formatExportDate = (value: string | Date | null | undefined): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return value.trim();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = String(d.getUTCFullYear());
  return `${day}/${month}/${year}`;
};

const formatAmount = (amount: number | null | undefined): string => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '';
  return amount.toFixed(2);
};

const buildExportFlatDataHtml = (
  reportId: number,
  records: JournalEntryDatRecordInput[],
): string => {
  const rowsHtml =
    records.length === 0
      ? `<tr><td colspan="9" style="text-align:center;padding:16px;color:#6b7280;">לא נמצאו רשומות</td></tr>`
      : records
          .map(
            (r, index) => `
      <tr>
        <td class="serial">${index + 1}</td>
        <td>${escapeHtml(r.debitAccountCode ?? '')}</td>
        <td>${escapeHtml(r.creditAccountCode ?? '')}</td>
        <td class="desc">${escapeHtml(r.paymentDescription ?? '')}</td>
        <td>${escapeHtml(formatExportDate(r.entryDate))}</td>
        <td>${escapeHtml(formatExportDate(r.valueDate))}</td>
        <td class="amount">${escapeHtml(formatAmount(r.amount))}</td>
        <td>${escapeHtml(r.documentNumber ?? '')}</td>
        <td>${escapeHtml(r.paymentReference ?? '')}</td>
      </tr>`,
          )
          .join('');

  return `
    <div class="export-root" dir="rtl">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;700&display=swap');
        .export-root {
          font-family: 'Noto Sans Hebrew', Arial, sans-serif;
          direction: rtl;
          color: #111827;
          background: #ffffff;
          padding: 16px 20px 24px;
          box-sizing: border-box;
          width: 1123px;
        }
        .header {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 14px;
        }
        .header-text {
          flex: 1;
          text-align: right;
        }
        .logo {
          height: 72px;
          width: auto;
          object-fit: contain;
          flex-shrink: 0;
        }
        .title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 4px;
          text-align: right;
        }
        .subtitle {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
          text-align: right;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        th, td {
          border: 1px solid #d1d5db;
          padding: 6px 8px;
          text-align: right;
          vertical-align: top;
        }
        th {
          background: #1e3a5f;
          color: #ffffff;
          font-weight: 700;
          white-space: nowrap;
        }
        tr:nth-child(even) td {
          background: #f8fafc;
        }
        td.serial {
          white-space: nowrap;
          text-align: center;
          width: 40px;
        }
        td.amount {
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
        }
        td.desc {
          max-width: 220px;
          word-break: break-word;
        }
      </style>
      <div class="header">
        <img class="logo" src="${harediLogo}" alt="לוגו מרכז חרדי" />
        <div class="header-text">
          <h1 class="title">ייצוא פקודות יומן — דיווח #${reportId}</h1>
          <p class="subtitle">סה״כ רשומות: ${records.length.toLocaleString('he-IL')}</p>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>מס״ד</th>
            <th>חובה</th>
            <th>זכות</th>
            <th>פרטים</th>
            <th>ת.פעולה</th>
            <th>ת.ערך</th>
            <th>סכום</th>
            <th>אסמכה 1</th>
            <th>אסמכתה 2</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  `;
};

const generateFlatDataPdf = async (
  reportId: number,
  records: JournalEntryDatRecordInput[],
): Promise<void> => {
  const html = `<!DOCTYPE html><html lang="he" dir="rtl"><head><meta charset="utf-8" /></head><body style="margin:0;background:#fff;">${buildExportFlatDataHtml(reportId, records)}</body></html>`;

  // Off-screen iframe keeps the rasterization completely invisible (same
  // pattern as study-certificate PDF) — never flashes content in the app UI.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;top:0;left:0;width:1123px;height:1600px;border:none;visibility:hidden;opacity:0;pointer-events:none;z-index:-9999;';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument;
  if (!iframeDoc) {
    document.body.removeChild(iframe);
    throw new Error('Failed to create PDF render iframe');
  }

  iframeDoc.open();
  iframeDoc.write(html);
  iframeDoc.close();

  try {
    await new Promise<void>((resolve) => {
      if (iframeDoc.readyState === 'complete') {
        resolve();
      } else {
        iframe.onload = () => resolve();
      }
    });

    await iframeDoc.fonts?.ready;
    // Allow webfont + logo to settle before rasterizing Hebrew text.
    await new Promise((resolve) => setTimeout(resolve, 400));

    const logoImg = iframeDoc.querySelector(
      'img.logo',
    ) as HTMLImageElement | null;
    if (logoImg && !logoImg.complete) {
      await new Promise<void>((resolve) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => resolve();
      });
    }

    const root = iframeDoc.querySelector('.export-root') as HTMLElement;
    if (!root) {
      throw new Error('PDF export root element not found');
    }

    const canvas = await html2canvas(root, {
      allowTaint: true,
      useCORS: true,
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: 1123,
    });

    // Landscape A4 — fits the wide flat-data columns better.
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    if (imgHeight > pageHeight) {
      let position = pageHeight;
      while (position < imgHeight) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
        position += pageHeight;
      }
    }

    pdf.save(`פקודות_יומן_דיווח_${reportId}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
};

export const exportJournalEntryReportFlatDataPdfOperation = async (
  reportId: number,
  setLoading: (loading: boolean) => void,
): Promise<boolean> => {
  if (!reportId) {
    toast.warn('חסר מזהה דיווח לייצוא');
    return false;
  }

  try {
    setLoading(true);

    const res = await appApi.getJournalEntryReportExportFlatData(reportId);

    if (res.data.errCode) {
      toast.warn(displayErrorText(res.data.errCode));
      setLoading(false);
      return false;
    }

    const records = res.data.body ?? [];
    if (records.length === 0) {
      toast.warn('לא נמצאו רשומות לייצוא בדיווח זה');
      setLoading(false);
      return false;
    }

    await generateFlatDataPdf(reportId, records);
    toast.success('קובץ ה-PDF הורד בהצלחה');
    setLoading(false);
    return true;
  } catch (e) {
    console.error('Export journal entry report flat data PDF failed:', e);
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setLoading(false);
    return false;
  }
};
